import React from 'react';
import { TrendingUp, CheckCircle, ArrowRight, AlertTriangle, Target } from 'lucide-react';
import ModernDocHeader from '../../components/documentation/ModernDocHeader';

const PipelineDocs: React.FC = () => {
  return (
    <div>
      <ModernDocHeader
        title="Pipeline & Stages"
        description="Master your sales pipeline with visual Kanban boards, custom stages, and powerful analytics to track leads from first contact to closed deals."
        icon={<TrendingUp className="w-6 h-6" />}
        badge="Essential"
        badgeColor="blue"
      />

      <div className="p-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 max-w-4xl">
          <div className="prose prose-blue dark:prose-invert max-w-none">

      {/* What You'll Learn */}
      <div className="not-prose bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Target className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-3">
              What You'll Learn
            </h3>
            <ul className="space-y-1 text-sm text-blue-800 dark:text-blue-300">
              <li>✓ Understanding the visual pipeline interface</li>
              <li>✓ Creating and customizing pipeline stages</li>
              <li>✓ Moving leads through the sales process</li>
              <li>✓ Tracking conversion rates and analytics</li>
              <li>✓ Automating stage transitions</li>
              <li>✓ Best practices for pipeline management</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>What is a Sales Pipeline?</h2>
      <p>
        A sales pipeline is a visual representation of your leads as they move through different stages
        of your sales process. It provides a clear overview of where each lead stands and helps you:
      </p>
      <ul>
        <li><strong>Visualize Progress</strong> - See all leads organized by their current stage</li>
        <li><strong>Identify Bottlenecks</strong> - Spot stages where leads get stuck</li>
        <li><strong>Forecast Revenue</strong> - Predict future sales based on pipeline health</li>
        <li><strong>Prioritize Efforts</strong> - Focus on leads most likely to convert</li>
        <li><strong>Track Team Performance</strong> - Monitor individual and team conversion rates</li>
      </ul>

      <h2>Pipeline Interface Overview</h2>

      <h3>Kanban Board View</h3>
      <p>
        Your pipeline is displayed as a Kanban board with columns representing each stage.
        Each card represents a lead, showing:
      </p>
      <ul>
        <li>Lead name and contact information</li>
        <li>Lead source and assigned team member</li>
        <li>Deal value (if specified)</li>
        <li>Days in current stage</li>
        <li>Last activity timestamp</li>
        <li>Quick action buttons</li>
      </ul>

      <div className="not-prose bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 my-6">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          <strong>💡 Pro Tip:</strong>
        </p>
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Use drag-and-drop to move leads between stages. Simply grab a lead card and drop it into
          the desired stage column. The system automatically logs the stage change.
        </p>
      </div>

      <h2>Default Pipeline Stages</h2>
      <p>Your CRM comes pre-configured with a standard sales pipeline:</p>

      <div className="not-prose space-y-3 my-6">
        <div className="flex items-start space-x-3 p-4 bg-white dark:bg-gray-800 border-l-4 border-gray-400 dark:border-gray-600 rounded-lg">
          <div className="flex-shrink-0 mt-1">
            <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center text-sm font-semibold">
              1
            </div>
          </div>
          <div>
            <p className="font-semibold text-gray-900 dark:text-white">New</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Freshly captured leads that haven't been contacted yet. All new leads start here automatically.
            </p>
          </div>
        </div>

        <div className="flex items-start space-x-3 p-4 bg-white dark:bg-gray-800 border-l-4 border-blue-400 dark:border-blue-600 rounded-lg">
          <div className="flex-shrink-0 mt-1">
            <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center text-sm font-semibold text-blue-600">
              2
            </div>
          </div>
          <div>
            <p className="font-semibold text-gray-900 dark:text-white">Contacted</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              First contact has been made (call, email, or message). Lead has acknowledged your outreach.
            </p>
          </div>
        </div>

        <div className="flex items-start space-x-3 p-4 bg-white dark:bg-gray-800 border-l-4 border-purple-400 dark:border-purple-600 rounded-lg">
          <div className="flex-shrink-0 mt-1">
            <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center text-sm font-semibold text-purple-600">
              3
            </div>
          </div>
          <div>
            <p className="font-semibold text-gray-900 dark:text-white">Qualified</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Lead meets your qualification criteria (budget, authority, need, timeline). Worth pursuing.
            </p>
          </div>
        </div>

        <div className="flex items-start space-x-3 p-4 bg-white dark:bg-gray-800 border-l-4 border-orange-400 dark:border-orange-600 rounded-lg">
          <div className="flex-shrink-0 mt-1">
            <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center text-sm font-semibold text-orange-600">
              4
            </div>
          </div>
          <div>
            <p className="font-semibold text-gray-900 dark:text-white">Proposal</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Formal proposal, quote, or presentation has been sent. Awaiting feedback or decision.
            </p>
          </div>
        </div>

        <div className="flex items-start space-x-3 p-4 bg-white dark:bg-gray-800 border-l-4 border-yellow-400 dark:border-yellow-600 rounded-lg">
          <div className="flex-shrink-0 mt-1">
            <div className="w-8 h-8 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center text-sm font-semibold text-yellow-600">
              5
            </div>
          </div>
          <div>
            <p className="font-semibold text-gray-900 dark:text-white">Negotiation</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Actively discussing terms, pricing, or contract details. Close to making a decision.
            </p>
          </div>
        </div>

        <div className="flex items-start space-x-3 p-4 bg-white dark:bg-gray-800 border-l-4 border-green-400 dark:border-green-600 rounded-lg">
          <div className="flex-shrink-0 mt-1">
            <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center text-sm font-semibold text-green-600">
              6
            </div>
          </div>
          <div>
            <p className="font-semibold text-gray-900 dark:text-white">Won</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Deal closed successfully! Customer has signed, paid, or committed. Revenue realized.
            </p>
          </div>
        </div>

        <div className="flex items-start space-x-3 p-4 bg-white dark:bg-gray-800 border-l-4 border-red-400 dark:border-red-600 rounded-lg">
          <div className="flex-shrink-0 mt-1">
            <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center text-sm font-semibold text-red-600">
              7
            </div>
          </div>
          <div>
            <p className="font-semibold text-gray-900 dark:text-white">Lost</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Deal didn't close. Lead went with competitor, budget constraints, or no longer interested.
            </p>
          </div>
        </div>
      </div>

      <h2>Customizing Pipeline Stages</h2>
      <p>
        While the default stages work for most businesses, you can customize them to match your
        specific sales process. Navigate to <code>Settings → Pipeline</code>
      </p>

      <h3>Adding a New Stage</h3>
      <ol>
        <li>Click "Add New Stage" button</li>
        <li>Enter stage name (e.g., "Demo Scheduled", "Trial Active")</li>
        <li>Choose stage color for visual identification</li>
        <li>Set stage position in the pipeline order</li>
        <li>Define if stage counts as "active opportunity"</li>
        <li>Click "Save" to create the stage</li>
      </ol>

      <h3>Editing Existing Stages</h3>
      <ol>
        <li>Click on the stage you want to edit</li>
        <li>Modify name, color, or position</li>
        <li>Update probability percentage (for revenue forecasting)</li>
        <li>Save changes</li>
      </ol>

      <div className="not-prose bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 my-6">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-yellow-800 dark:text-yellow-300 mb-2">
              <strong>⚠️ Important:</strong>
            </p>
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              Deleting a stage will move all leads in that stage to "New". Consider moving leads
              manually before deleting a stage.
            </p>
          </div>
        </div>
      </div>

      <h3>Reordering Stages</h3>
      <p>Drag and drop stages in the settings to reorder them. The pipeline view will update automatically.</p>

      <h2>Moving Leads Through the Pipeline</h2>

      <h3>Manual Movement</h3>
      <p>There are several ways to move a lead to a different stage:</p>

      <div className="not-prose grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Drag & Drop</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Click and drag a lead card from one stage column to another. Fastest method for visual pipeline management.
          </p>
        </div>

        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Lead Detail Modal</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Open lead details, click the stage dropdown in the header, and select the new stage.
          </p>
        </div>

        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Bulk Actions</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Select multiple leads using checkboxes, then use "Change Stage" bulk action to move them together.
          </p>
        </div>
      </div>

      <h3>Automatic Stage Transitions</h3>
      <p>Configure automatic stage changes based on lead actions or time:</p>
      <ul>
        <li><strong>Activity-Based</strong> - Move to "Contacted" when first call is logged</li>
        <li><strong>Time-Based</strong> - Move to "Lost" after 30 days of inactivity</li>
        <li><strong>Score-Based</strong> - Move to "Qualified" when lead score reaches threshold</li>
        <li><strong>Integration-Based</strong> - Move to "Proposal" when quote is sent via email</li>
      </ul>

      <h2>Pipeline Analytics & Metrics</h2>

      <h3>Key Metrics Tracked</h3>
      <div className="not-prose">
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden my-6">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">Metric</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              <tr>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Conversion Rate</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                  Percentage of leads that move from New to Won
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Average Deal Size</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                  Mean value of all Won deals
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Sales Cycle Length</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                  Average time from New to Won
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Stage Velocity</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                  Average days leads spend in each stage
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Win Rate by Source</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                  Conversion rate broken down by lead source
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Pipeline Value</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                  Total potential revenue in active stages
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <h3>Viewing Analytics</h3>
      <p>Access pipeline analytics from <code>Reports → Pipeline</code>:</p>
      <ul>
        <li>Pipeline overview chart showing lead distribution</li>
        <li>Conversion funnel visualization</li>
        <li>Stage velocity trends over time</li>
        <li>Win/loss analysis with reasons</li>
        <li>Team performance comparison</li>
        <li>Revenue forecasting based on pipeline health</li>
      </ul>

      <h2>Best Practices for Pipeline Management</h2>

      <div className="not-prose bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 my-8">
        <div className="flex items-start space-x-3">
          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-3">
              Pipeline Hygiene Checklist
            </h3>
            <ul className="space-y-2 text-sm text-green-800 dark:text-green-300">
              <li>✓ <strong>Review daily</strong> - Check pipeline every morning for new leads and updates</li>
              <li>✓ <strong>Update promptly</strong> - Move leads immediately after calls or meetings</li>
              <li>✓ <strong>Add context</strong> - Log notes when changing stages explaining why</li>
              <li>✓ <strong>Clean weekly</strong> - Remove stale leads or move inactive ones to Lost</li>
              <li>✓ <strong>Track metrics</strong> - Monitor conversion rates and identify bottlenecks</li>
              <li>✓ <strong>Set reminders</strong> - Schedule follow-ups for leads in key stages</li>
              <li>✓ <strong>Define criteria</strong> - Create clear rules for when to advance leads</li>
            </ul>
          </div>
        </div>
      </div>

      <h3>Stage Advancement Guidelines</h3>
      <p>Establish clear criteria for moving leads between stages:</p>

      <div className="not-prose space-y-3 my-6">
        <details className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <summary className="p-4 font-medium text-gray-900 dark:text-white cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50">
            New → Contacted
          </summary>
          <div className="px-4 pb-4 text-sm text-gray-600 dark:text-gray-400">
            <p><strong>Required:</strong></p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>At least one call attempt or email sent</li>
              <li>Lead has acknowledged contact (answered call, replied to email)</li>
              <li>Initial conversation notes logged</li>
            </ul>
          </div>
        </details>

        <details className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <summary className="p-4 font-medium text-gray-900 dark:text-white cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50">
            Contacted → Qualified
          </summary>
          <div className="px-4 pb-4 text-sm text-gray-600 dark:text-gray-400">
            <p><strong>Required (BANT Framework):</strong></p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li><strong>Budget:</strong> Lead can afford your product/service</li>
              <li><strong>Authority:</strong> Lead has decision-making power</li>
              <li><strong>Need:</strong> Clear pain point your solution addresses</li>
              <li><strong>Timeline:</strong> Defined timeframe for purchase</li>
            </ul>
          </div>
        </details>

        <details className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <summary className="p-4 font-medium text-gray-900 dark:text-white cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50">
            Qualified → Proposal
          </summary>
          <div className="px-4 pb-4 text-sm text-gray-600 dark:text-gray-400">
            <p><strong>Required:</strong></p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Completed needs assessment or demo</li>
              <li>Formal proposal/quote requested by lead</li>
              <li>All requirements and pricing discussed</li>
              <li>Proposal document sent and acknowledged</li>
            </ul>
          </div>
        </details>

        <details className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <summary className="p-4 font-medium text-gray-900 dark:text-white cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50">
            Proposal → Negotiation
          </summary>
          <div className="px-4 pb-4 text-sm text-gray-600 dark:text-gray-400">
            <p><strong>Required:</strong></p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Lead actively discussing terms or pricing</li>
              <li>Counter-offer or modification request received</li>
              <li>Contract review in progress</li>
              <li>Verbal commitment to move forward</li>
            </ul>
          </div>
        </details>

        <details className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <summary className="p-4 font-medium text-gray-900 dark:text-white cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50">
            Negotiation → Won
          </summary>
          <div className="px-4 pb-4 text-sm text-gray-600 dark:text-gray-400">
            <p><strong>Required:</strong></p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Contract signed by both parties</li>
              <li>Payment received (or payment schedule agreed)</li>
              <li>Project kickoff scheduled</li>
              <li>Deal value and close date recorded</li>
            </ul>
          </div>
        </details>

        <details className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <summary className="p-4 font-medium text-gray-900 dark:text-white cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50">
            Any Stage → Lost
          </summary>
          <div className="px-4 pb-4 text-sm text-gray-600 dark:text-gray-400">
            <p><strong>Common Reasons:</strong></p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Lead chose competitor</li>
              <li>Budget constraints or no budget</li>
              <li>No response after multiple follow-ups</li>
              <li>Timeline pushed indefinitely</li>
              <li>Project cancelled or requirements changed</li>
              <li><strong>Always log reason</strong> for future analysis</li>
            </ul>
          </div>
        </details>
      </div>

      <h3>Common Pipeline Workflows</h3>

      <h4>Morning Pipeline Review (10 minutes)</h4>
      <ol>
        <li>Check "New" stage for overnight leads → Assign and plan first contact</li>
        <li>Review "Contacted" stage → Schedule follow-up calls for today</li>
        <li>Check "Proposal" and "Negotiation" → Priority follow-ups to close deals</li>
        <li>Scan for overdue follow-ups → Move to Lost or re-engage</li>
      </ol>

      <h4>Weekly Pipeline Cleanup (30 minutes)</h4>
      <ol>
        <li>Identify leads with no activity in 14+ days</li>
        <li>Send re-engagement email or make final call attempt</li>
        <li>Move unresponsive leads to Lost (with reason logged)</li>
        <li>Review conversion rates by source → Adjust marketing spend</li>
        <li>Update deal values for accurate forecasting</li>
      </ol>

      <h4>Monthly Pipeline Analysis (1 hour)</h4>
      <ol>
        <li>Generate pipeline report for the month</li>
        <li>Analyze win/loss reasons → Identify patterns</li>
        <li>Calculate average sales cycle → Look for improvements</li>
        <li>Review stage conversion rates → Find bottlenecks</li>
        <li>Forecast next month's revenue based on pipeline</li>
        <li>Share insights with team in monthly meeting</li>
      </ol>

      <h2>Advanced Pipeline Features</h2>

      <h3>Multiple Pipelines</h3>
      <p>
        Create separate pipelines for different product lines or business units:
      </p>
      <ul>
        <li><strong>Enterprise Sales Pipeline</strong> - Long sales cycle, higher value deals</li>
        <li><strong>SMB Sales Pipeline</strong> - Shorter cycle, lower value, higher volume</li>
        <li><strong>Renewal Pipeline</strong> - Existing customer renewals and upsells</li>
        <li><strong>Partner Pipeline</strong> - Channel partner or referral deals</li>
      </ul>

      <h3>Deal Value & Forecasting</h3>
      <p>
        Add estimated deal values to leads for revenue forecasting:
      </p>
      <ol>
        <li>Open lead detail modal</li>
        <li>Navigate to "Deal Info" tab</li>
        <li>Enter estimated value and expected close date</li>
        <li>System calculates weighted forecast based on stage probability</li>
      </ol>

      <div className="not-prose bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 my-4">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          <strong>Example Forecast Calculation:</strong>
        </p>
        <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
          Lead in "Proposal" stage with ₹100,000 value and 40% probability:
        </p>
        <p className="text-sm font-mono text-gray-900 dark:text-white">
          Weighted Value = ₹100,000 × 0.40 = ₹40,000
        </p>
      </div>

      <h3>Pipeline Automation Rules</h3>
      <p>Create smart automation to keep your pipeline flowing:</p>
      <ul>
        <li><strong>Auto-assign</strong> new leads to team members via Round Robin</li>
        <li><strong>Auto-reminder</strong> when lead sits in stage for X days</li>
        <li><strong>Auto-email</strong> when lead reaches certain stage (e.g., welcome email when Won)</li>
        <li><strong>Auto-task</strong> creation when lead enters stage (e.g., "Send proposal" task)</li>
      </ul>

      <h2>Troubleshooting</h2>

      <div className="not-prose bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700 my-4">
        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Leads not appearing in pipeline?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400 space-y-2">
            <p><strong>Check:</strong></p>
            <ul className="list-disc list-inside space-y-1">
              <li>Pipeline filter settings - might be filtered by team member or source</li>
              <li>Date range filter - might exclude recent leads</li>
              <li>Lead stage - "Won" and "Lost" leads may be hidden by default</li>
              <li>User permissions - Sales reps only see assigned leads</li>
            </ul>
          </div>
        </details>

        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Can't drag and drop leads between stages?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <p><strong>Possible causes:</strong></p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Browser compatibility - works best in Chrome, Firefox, Edge</li>
              <li>User permissions - you may not have permission to modify lead stages</li>
              <li>Lead is locked - some leads may be locked during data sync</li>
              <li>Try using the stage dropdown in lead detail as alternative</li>
            </ul>
          </div>
        </details>

        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Pipeline metrics not updating?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <p>Analytics are calculated every hour. If you need immediate refresh:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Click the "Refresh" button in analytics dashboard</li>
              <li>Clear browser cache if data seems stale</li>
              <li>Contact support if metrics are more than 2 hours old</li>
            </ul>
          </div>
        </details>

        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            How to restore a lead moved to Lost by mistake?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <p><strong>Easy fix:</strong></p>
            <ol className="list-decimal list-inside space-y-1 mt-2">
              <li>Go to All Leads view (not Pipeline)</li>
              <li>Filter by "Lost" status</li>
              <li>Find the lead and open detail modal</li>
              <li>Change stage back to appropriate stage</li>
              <li>Lead will reappear in pipeline immediately</li>
            </ol>
          </div>
        </details>
      </div>

      <h2>Next Steps</h2>

      <div className="not-prose space-y-3 mt-6">
        <a
          href="/docs/lead-management"
          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Master Lead Management</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Learn advanced techniques for managing and tracking leads
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </a>

        <a
          href="/docs/round-robin"
          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Set Up Round Robin Assignment</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Automatically distribute leads among team members
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </a>

        <a
          href="/docs/drip-campaigns"
          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Create Drip Campaigns</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Automate email sequences based on pipeline stages
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </a>
      </div>

      <div className="not-prose bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 mt-8">
        <div className="flex items-start space-x-3">
          <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-2">
              Pipeline Mastery Unlocked!
            </h3>
            <p className="text-green-800 dark:text-green-300">
              You now have all the knowledge to effectively manage your sales pipeline. Remember:
              consistent pipeline hygiene and clear stage advancement criteria are the keys to
              predictable revenue and high conversion rates.
            </p>
          </div>
        </div>
      </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default PipelineDocs;
